import React from 'react';
import { Droplet, TrendingUp, BarChart3, Shield, Target, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import descriptionImage from '../../assets/team-upbeat-business-partners-inspecting-solar-plant-machinery_482257-119652.jpg_qnpihf.jpg';

const OilAndGasPage = () => {
  const services = [
    {
      icon: Droplet,
      title: 'Energy Sector Investment',
      description: 'Explore investment opportunities in the energy sector with our specialized oil and gas portfolio options designed for long-term growth.'
    },
    {
      icon: BarChart3,
      title: 'Market Analysis',
      description: 'Comprehensive analysis of oil and gas market trends, price movements, and industry developments to inform your investment decisions.'
    },
    {
      icon: TrendingUp,
      title: 'Portfolio Diversification',
      description: 'Diversify your investment portfolio with oil and gas assets, providing exposure to the valuable commodity market and energy sector.'
    },
    {
      icon: Shield,
      title: 'Risk Management',
      description: 'Strategic risk management approaches for oil and gas investments, including hedging strategies and portfolio protection measures.'
    },
    {
      icon: Target,
      title: 'Investment Opportunities',
      description: 'Access carefully selected oil and gas investment opportunities with insights into this valuable commodity market and growth potential.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src="/images/refinery-3613526_1280.jpg"
            alt="Oil and Gas"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Oil and Gas</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Sectors</span> / <span className="text-gold-400">Oil and Gas</span>
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Energy Sector <span className="text-gold-600">Investments</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Explore investment opportunities in the energy sector with our specialized oil and gas portfolio options. We provide insights into this valuable commodity market and help you capitalize on energy sector growth potential.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                The oil and gas sector offers unique investment opportunities for portfolio diversification and long-term growth. Our expert team provides comprehensive market analysis, risk management strategies, and access to carefully selected investment opportunities in this dynamic energy market.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src={descriptionImage}
                  alt="Oil and Gas Investment"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="mb-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-4">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Invest in Oil and Gas?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Explore energy sector investment opportunities and diversify your portfolio with oil and gas assets. 
              Get expert insights and strategic guidance for your energy investments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://online.amdconsults.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-600 hover:bg-gold-700 text-white font-bold rounded-full transition-colors duration-300"
              >
                Start Investing
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <Link 
                to="/#investment-sectors"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300"
              >
                View All Sectors
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OilAndGasPage;

